import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
    Box, Paper, TextField, Button, Typography, 
    Container, Avatar, Chip, Alert, CircularProgress,
    Divider, Dialog, DialogTitle, DialogContent,
    DialogContentText, DialogActions
} from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import SecurityIcon from '@mui/icons-material/Security';
import { authService } from '../services/auth';

// Official Google Logo SVG
const GoogleIcon = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" style={{ marginRight: 10 }}>
        <path fill="#4285F4" d="M23.745 12.27c0-.7-.06-1.4-.19-2.07H12v4.51h6.6c-.29 1.52-1.14 2.8-2.4 3.66v3.05h3.88c2.27-2.09 3.66-5.17 3.66-9.15z"/>
        <path fill="#34A853" d="M12 24c3.24 0 5.95-1.08 7.93-2.91l-3.88-3.05c-1.08.72-2.45 1.16-4.05 1.16-3.12 0-5.77-2.1-6.72-4.93H1.24v3.15C3.26 21.36 7.33 24 12 24z"/>
        <path fill="#FBBC05" d="M5.28 14.27c-.25-.72-.38-1.49-.38-2.27s.14-1.55.38-2.27V6.58H1.24C.45 8.15 0 9.99 0 12s.45 3.85 1.24 5.42l4.04-3.15z"/>
        <path fill="#EA4335" d="M12 4.75c1.77 0 3.35.61 4.6 1.8l3.42-3.42C17.95 1.19 15.24 0 12 0 7.33 0 3.26 2.64 1.24 6.58l4.04 3.15c.95-2.83 3.6-4.98 6.72-4.98z"/>
    </svg>
);

// Apple Face ID / Passkey Icon SVG
const FaceIdIcon = () => (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: 10 }}>
        <path d="M7 3H5a2 2 0 0 0-2 2v2" />
        <path d="M17 3h2a2 2 0 0 1 2 2v2" />
        <path d="M21 17v2a2 2 0 0 1-2 2h-2" />
        <path d="M3 17v2a2 2 0 0 0 2 2h2" />
        <path d="M9 9h.01" />
        <path d="M15 9h.01" />
        <path d="M10 13a2 2 0 0 0 4 0" />
        <path d="M8 17a5 5 0 0 0 8 0" />
    </svg>
);

const LoginPage = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [authMethod, setAuthMethod] = useState(''); // 'password', 'google', 'faceid'

    // Biometrics / Face ID states
    const [biometricsAvailable, setBiometricsAvailable] = useState(false);
    const [biometricsConfigured, setBiometricsConfigured] = useState(false);
    const [biometricEmail, setBiometricEmail] = useState('');
    const [promptBiometricsDialog, setPromptBiometricsDialog] = useState(false);
    const [loggedInEmail, setLoggedInEmail] = useState('');

    useEffect(() => {
        // Detect Face ID / Touch ID platform capabilities
        authService.isBiometricsAvailable().then(avail => {
            setBiometricsAvailable(avail);
            const isConfigured = authService.isBiometricsConfigured();
            setBiometricsConfigured(isConfigured);
            if (isConfigured) {
                setBiometricEmail(authService.getBiometricEmail());
            }
        });

        const lastEmail = localStorage.getItem('lastAdminEmail');
        if (lastEmail) {
            setEmail(lastEmail);
        }
    }, []);

    const finishLogin = (userEmail) => {
        // If Face ID is supported on this device but not yet configured, ask to enable it!
        if (biometricsAvailable && !authService.isBiometricsConfigured()) {
            setLoggedInEmail(userEmail || email);
            setPromptBiometricsDialog(true);
        } else {
            const from = location.state?.from?.pathname || '/dashboard';
            navigate(from, { replace: true });
        }
    };

    const handlePasswordSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setIsLoading(true);
        setAuthMethod('password');

        try {
            const result = await authService.login(email, password);
            finishLogin(result.user?.email || email);
        } catch (error) {
            console.error('Login error:', error);
            setError(error.response?.data?.error || error.message || 'Invalid administrator credentials');
        } finally {
            setIsLoading(false);
            setAuthMethod('');
        }
    };

    const handleGoogleSignIn = async () => {
        setError('');
        setIsLoading(true);
        setAuthMethod('google');

        try {
            const result = await authService.loginWithGoogle();
            finishLogin(result.user?.email);
        } catch (error) {
            console.error('Google Sign-In error:', error);
            if (error.code === 'auth/popup-closed-by-user') {
                setError('Google sign-in popup was closed.');
            } else {
                setError(error.response?.data?.error || error.message || 'Google authentication failed');
            }
        } finally {
            setIsLoading(false);
            setAuthMethod('');
        }
    };

    const handleFaceIdSignIn = async () => {
        setError('');
        setIsLoading(true);
        setAuthMethod('faceid');

        try {
            const result = await authService.loginWithBiometrics();
            const from = location.state?.from?.pathname || '/dashboard';
            navigate(from, { replace: true });
        } catch (error) {
            console.error('Face ID login error:', error);
            setError(error.message || 'Face ID verification failed. Please use Google or Password.');
        } finally {
            setIsLoading(false);
            setAuthMethod('');
        }
    };

    const handleSetupFaceId = async () => {
        try {
            await authService.registerBiometrics(loggedInEmail || email);
            setBiometricsConfigured(true);
            setPromptBiometricsDialog(false);
            const from = location.state?.from?.pathname || '/dashboard';
            navigate(from, { replace: true });
        } catch (error) {
            console.error('Error enabling Face ID:', error);
            setPromptBiometricsDialog(false);
            const from = location.state?.from?.pathname || '/dashboard';
            navigate(from, { replace: true });
        }
    };

    const handleSkipFaceId = () => {
        setPromptBiometricsDialog(false);
        const from = location.state?.from?.pathname || '/dashboard';
        navigate(from, { replace: true });
    };

    return (
        <Box sx={{
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'radial-gradient(ellipse at 50% 25%, #EEF2FF 0%, #F8FAFC 60%, #E2E8F0 100%)',
            p: 2
        }}>
            <Container component="main" maxWidth="xs">
                <Paper 
                    elevation={0}
                    sx={{ 
                        p: { xs: 3, sm: 4.5 }, 
                        borderRadius: 4,
                        border: '1px solid #E2E8F0',
                        boxShadow: '0 20px 25px -5px rgba(15, 23, 42, 0.08), 0 8px 10px -6px rgba(15, 23, 42, 0.04)',
                        backgroundColor: '#FFFFFF'
                    }}
                >
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 3 }}>
                        <Avatar sx={{ 
                            m: 1, 
                            width: 52, 
                            height: 52, 
                            background: 'linear-gradient(135deg, #4F46E5 0%, #7C3AED 100%)',
                            boxShadow: '0 4px 14px rgba(79, 70, 229, 0.35)'
                        }}>
                            <LockOutlinedIcon sx={{ fontSize: 28 }} />
                        </Avatar>
                        
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                            <Typography component="h1" variant="h5" sx={{ fontWeight: 800, letterSpacing: '-0.02em' }}>
                                GET·CODE
                            </Typography>
                            <Chip 
                                label="PRO" 
                                size="small" 
                                sx={{ 
                                    height: 20, 
                                    fontSize: '0.65rem', 
                                    fontWeight: 800, 
                                    bgcolor: 'primary.main', 
                                    color: '#fff' 
                                }} 
                            />
                        </Box>

                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, textAlign: 'center' }}>
                            Administrator Authentication Portal
                        </Typography>
                    </Box>

                    {error && (
                        <Alert severity="error" sx={{ mb: 2.5, borderRadius: 2 }}>
                            {error}
                        </Alert>
                    )}

                    {/* 1. Face ID / Touch ID One-Tap Sign In (If device is paired) */}
                    {biometricsAvailable && biometricsConfigured && (
                        <Box sx={{ mb: 2.5 }}>
                            <Button
                                fullWidth
                                variant="contained"
                                size="large"
                                onClick={handleFaceIdSignIn}
                                disabled={isLoading}
                                sx={{
                                    py: 1.4,
                                    fontSize: '0.95rem',
                                    fontWeight: 700,
                                    borderRadius: 2.5,
                                    backgroundColor: '#0F172A',
                                    color: '#FFFFFF',
                                    '&:hover': {
                                        backgroundColor: '#1E293B'
                                    },
                                    boxShadow: '0 4px 14px rgba(15, 23, 42, 0.25)'
                                }}
                            >
                                {isLoading && authMethod === 'faceid' ? (
                                    <CircularProgress size={22} color="inherit" />
                                ) : (
                                    <>
                                        <FaceIdIcon />
                                        Sign in with Face ID
                                    </>
                                )}
                            </Button>
                            {biometricEmail && (
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mt: 0.75 }}>
                                    Paired for {biometricEmail}
                                </Typography>
                            )}
                        </Box>
                    )}

                    {/* 2. Google Account Sign In Button */}
                    <Button
                        fullWidth
                        variant="outlined"
                        size="large"
                        onClick={handleGoogleSignIn}
                        disabled={isLoading}
                        sx={{
                            py: 1.3,
                            mb: 2.5,
                            fontSize: '0.92rem',
                            fontWeight: 600,
                            borderRadius: 2.5,
                            borderColor: '#CBD5E1',
                            color: '#1E293B',
                            backgroundColor: '#FFFFFF',
                            '&:hover': {
                                backgroundColor: '#F8FAFC',
                                borderColor: '#94A3B8'
                            },
                            boxShadow: '0 1px 3px rgba(0,0,0,0.06)'
                        }}
                    >
                        {isLoading && authMethod === 'google' ? (
                            <CircularProgress size={22} color="inherit" />
                        ) : (
                            <>
                                <GoogleIcon />
                                Sign in with Google
                            </>
                        )}
                    </Button>

                    <Divider sx={{ my: 2.5 }}>
                        <Typography variant="caption" sx={{ color: '#94A3B8', fontWeight: 600, px: 1 }}>
                            OR SIGN IN WITH PASSWORD
                        </Typography>
                    </Divider>

                    {/* 3. Password Authentication Form */}
                    <form onSubmit={handlePasswordSubmit}>
                        <TextField
                            margin="normal"
                            required
                            fullWidth
                            id="email"
                            label="Admin Email Address"
                            name="email"
                            autoComplete="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            sx={{ mb: 1.5 }}
                        />
                        <TextField
                            margin="normal"
                            required
                            fullWidth
                            name="password"
                            label="Password"
                            type="password"
                            id="password"
                            autoComplete="current-password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            sx={{ mb: 2.5 }}
                        />
                        <Button
                            type="submit"
                            fullWidth
                            variant="contained"
                            color="primary"
                            size="large"
                            disabled={isLoading}
                            sx={{ 
                                py: 1.3, 
                                fontSize: '0.95rem',
                                fontWeight: 700,
                                borderRadius: 2.5,
                                boxShadow: '0 4px 14px rgba(79, 70, 229, 0.3)'
                            }}
                        >
                            {isLoading && authMethod === 'password' ? (
                                <CircularProgress size={24} color="inherit" />
                            ) : (
                                'Sign In with Password'
                            )}
                        </Button>
                    </form>

                    {/* Biometrics Setup Prompt link if available but not configured */}
                    {biometricsAvailable && !biometricsConfigured && (
                        <Box sx={{ mt: 2, textAlign: 'center' }}>
                            <Typography variant="caption" sx={{ color: '#64748B' }}>
                                💡 Face ID / Touch ID is supported on this browser. You can enable it after signing in.
                            </Typography>
                        </Box>
                    )}

                    <Box sx={{ mt: 3.5, pt: 2, borderTop: '1px solid #F1F5F9', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.75 }}>
                        <SecurityIcon sx={{ fontSize: 16, color: '#94A3B8' }} />
                        <Typography variant="caption" color="text.secondary">
                            Secured by Firebase Admin & WebAuthn Biometrics
                        </Typography>
                    </Box>
                </Paper>
            </Container>

            {/* Dialog to prompt user to pair Face ID right after login */}
            <Dialog 
                open={promptBiometricsDialog} 
                onClose={handleSkipFaceId}
                PaperProps={{
                    sx: { borderRadius: 3.5, p: 1, maxWidth: 420 }
                }}
            >
                <DialogTitle sx={{ fontWeight: 800, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <FaceIdIcon />
                    Enable Face ID Sign-in?
                </DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ color: '#475569', fontSize: '0.92rem' }}>
                        Your device supports biometric authentication (Face ID / Touch ID). 
                        Would you like to enable 1-tap sign-in on this device for <strong>{loggedInEmail}</strong>?
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ pb: 2, px: 3 }}>
                    <Button onClick={handleSkipFaceId} sx={{ color: '#64748B', fontWeight: 600 }}>
                        Maybe Later
                    </Button>
                    <Button 
                        onClick={handleSetupFaceId} 
                        variant="contained" 
                        sx={{ 
                            fontWeight: 700, 
                            borderRadius: 2,
                            background: 'linear-gradient(135deg, #0F172A 0%, #1E293B 100%)' 
                        }}
                    >
                        Enable Face ID
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default LoginPage;